/**
 * amenities-mapper — pure grouping / ordering / humanizing helpers for the
 * amenities picker (SCRUM-141 / DS-010-5).
 *
 * Grouping is DRIVEN BY THE CATALOG RESPONSE, never a hardcoded category list:
 * a NEW backend category renders (with the humanized-label + graceful-icon
 * fallback) without any FE change. The GROUP ORDER is the category's index in
 * `AMENITY_CATEGORY_VALUES` (the validators' source-of-truth taxonomy);
 * unknown categories are appended AFTER all known ones, sorted alphabetically
 * so their order is deterministic.
 *
 * These functions are pure and side-effect free — tested in isolation per the
 * slice's `dto-mapper` / `ownership-mapper` conventions.
 */

import type { components } from '@artemis/contracts';
import { AMENITY_CATEGORY_VALUES } from '@artemis/validators/policies';

type AmenityCatalogItemDto = components['schemas']['AmenityCatalogItemDto'];

/**
 * A catalog category group: the raw category key plus its amenities sorted by
 * label. `amenities` holds the ORIGINAL item objects (no clone) so identity is
 * preserved for React keys and referential-equality checks.
 */
export interface CatalogGroup {
  readonly category: string;
  readonly amenities: readonly AmenityCatalogItemDto[];
}

/**
 * Index of each known category, for O(1) rank lookup. Unknown categories are
 * not present and rank AFTER all known ones (see `categoryRank`).
 */
const CATEGORY_INDEX: ReadonlyMap<string, number> = new Map(
  AMENITY_CATEGORY_VALUES.map((cat, i) => [cat, i]),
);

/**
 * Sort rank for a category. Known categories rank by their taxonomy index
 * (0..12); unknown categories share a sentinel rank ABOVE every known index so
 * they sort last, then tie-break alphabetically (handled by the comparator).
 */
function categoryRank(category: string): number {
  const known = CATEGORY_INDEX.get(category);
  return known === undefined ? AMENITY_CATEGORY_VALUES.length : known;
}

/**
 * Comparator: known-by-index first, unknowns appended alphabetically. Two
 * unknowns compare by `localeCompare` (stable, deterministic); two knowns can
 * never tie (distinct indices).
 */
function compareCategory(a: string, b: string): number {
  const rankDelta = categoryRank(a) - categoryRank(b);
  if (rankDelta !== 0) {
    return rankDelta;
  }
  // Same rank ⇒ both unknown ⇒ alphabetical tie-break.
  return a.localeCompare(b);
}

/**
 * Group a flat catalog into per-category groups. Group order = taxonomy index
 * (unknowns appended alphabetically); within each group, amenities are sorted
 * by `label` (`localeCompare`). Original item objects are preserved.
 */
export function groupCatalog(
  items: readonly AmenityCatalogItemDto[],
): CatalogGroup[] {
  const byCategory = new Map<string, AmenityCatalogItemDto[]>();
  for (const item of items) {
    const bucket = byCategory.get(item.category);
    if (bucket === undefined) {
      byCategory.set(item.category, [item]);
    } else {
      bucket.push(item);
    }
  }

  return [...byCategory.keys()].sort(compareCategory).map((category) => ({
    category,
    amenities: [...(byCategory.get(category) as AmenityCatalogItemDto[])].sort(
      (a, b) => a.label.localeCompare(b.label),
    ),
  }));
}

/**
 * Reorder an already-grouped list by the same category rule (known-by-index
 * first, unknowns appended alphabetically). Generic over any `{ category }`
 * shape so it serves both the catalog groups and the server-grouped
 * property-amenity groups. Returns a NEW array; the group objects are the
 * originals (no clone).
 */
export function orderGroupsByCategory<T extends { readonly category: string }>(
  groups: readonly T[],
): T[] {
  return [...groups].sort((a, b) => compareCategory(a.category, b.category));
}

/**
 * Display label for the 13 known categories. Override map (with ampersands /
 * proper casing that a naive title-case cannot reproduce); unknown categories
 * fall through to `titleCase`.
 */
const CATEGORY_LABEL: Readonly<Record<string, string>> = {
  basics: 'Basics',
  bathroom: 'Bathroom',
  'bedroom-and-laundry': 'Bedroom & Laundry',
  entertainment: 'Entertainment',
  family: 'Family',
  'heating-and-cooling': 'Heating & Cooling',
  'home-safety': 'Home Safety',
  'internet-and-office': 'Internet & Office',
  'kitchen-and-dining': 'Kitchen & Dining',
  location: 'Location',
  outdoor: 'Outdoor',
  'parking-and-facilities': 'Parking & Facilities',
  accessibility: 'Accessibility',
};

/**
 * Title-case fallback for an unknown category: split on `-`, capitalize the
 * first letter of each word, join with a space. `'foo-bar'` → `'Foo Bar'`.
 */
function titleCase(category: string): string {
  return category
    .split('-')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Human-readable label for a category. Known categories use the override map;
 * unknown categories get the title-case fallback so a new BE category renders
 * a sensible label with zero FE change.
 */
export function humanizeCategory(category: string): string {
  return CATEGORY_LABEL[category] ?? titleCase(category);
}
